import React, { useState, useRef } from "react";
import api from "../api/educentialApi";
import { X, UploadCloud, File, CheckCircle, AlertCircle } from "lucide-react";

export default function UploadModal({ isOpen, onClose, onUploadSuccess, title = "Upload Document" }) {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const validateFile = (selectedFile) => {
    setError("");
    const allowedExtensions = ["pdf", "jpg", "jpeg", "png"];
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      setError("Invalid file format. Only PDF, JPG, JPEG, and PNG files are allowed.");
      return false;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setError("File is too large. Maximum size allowed is 10MB.");
      return false;
    }

    return true;
  };

  // Handle file drops
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
      }
    }
  };

  // Handle manual input selections
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
      }
    }
  };

  // Trigger file selection natively
  const onButtonClick = () => {
    fileInputRef.current.click();
  };

  // Upload file to server
  const handleUploadSubmit = async () => {
    if (!file) {
      setError("Please select a file to upload first.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setError("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/api/files/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percent);
        },
      });

      if (res.status === 201 && res.data) {
        setSuccess(true);
        setTimeout(() => {
          onUploadSuccess(res.data.fileId, res.data.filename, res.data.url);
          // Reset modal state
          setFile(null);
          setSuccess(false);
          setProgress(0);
          onClose();
        }, 1200);
      } else {
        throw new Error("Invalid server response.");
      }
    } catch (err) {
      console.error("Upload error details:", err);
      const errMsg = err.response?.data?.error || err.message || "Failed to upload document.";
      setError(errMsg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-secondary-950/80 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal Box */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-2xl max-w-md w-full shadow-strong animate-scale-in relative z-10 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-secondary-800">
          <h3 className="text-base font-display font-bold text-white uppercase tracking-wide">
            {title}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 hover:bg-secondary-850 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6">
          {error && (
            <div className="mb-4 flex items-start space-x-2.5 bg-red-950/40 border border-red-800/60 text-red-300 p-3.5 rounded-xl text-xs leading-relaxed animate-slide-down">
              <AlertCircle className="w-4 h-4 shrink-0 text-red-400 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-3 animate-fade-in">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center border border-emerald-500/20 text-emerald-400">
                <CheckCircle className="w-10 h-10 animate-scale-in" />
              </div>
              <h4 className="text-base font-display font-bold text-white">Upload Successful!</h4>
              <p className="text-gray-500 text-xs font-sans text-center">
                Syncing file records, please wait...
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Drag/Drop area */}
              <div
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl py-10 px-4 text-center cursor-pointer transition-all duration-300 ${
                  dragActive
                    ? "border-primary-500 bg-primary-950/10 scale-[1.01]"
                    : "border-secondary-800 bg-secondary-950/40 hover:border-secondary-750"
                }`}
                onClick={onButtonClick}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                />

                {file ? (
                  <div className="space-y-2.5 flex flex-col items-center">
                    <div className="w-12 h-12 bg-secondary-850 rounded-xl flex items-center justify-center border border-secondary-800 text-primary-400">
                      <File className="w-6 h-6 animate-float" />
                    </div>
                    <div className="max-w-[240px] truncate text-sm font-semibold text-white">
                      {file.name}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      Size: {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="w-12 h-12 bg-secondary-900 border border-secondary-800 rounded-xl mx-auto flex items-center justify-center text-gray-400">
                      <UploadCloud className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        Drag and drop your file here
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        or click to browse from device
                      </p>
                    </div>
                    <div className="text-[10px] text-gray-600 font-sans tracking-wide">
                      PDF, JPG, JPEG, PNG • MAX 10MB
                    </div>
                  </div>
                )}
              </div>

              {/* Uploading progress widget */}
              {uploading && (
                <div className="space-y-2 animate-slide-up">
                  <div className="flex justify-between text-xs text-gray-400 font-semibold">
                    <span>Uploading document...</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full bg-secondary-950 h-2 rounded-full overflow-hidden p-[1px] border border-secondary-800">
                    <div 
                      className="bg-brand-gradient h-full rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Controls */}
              {!uploading && (
                <div className="flex space-x-3.5">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 py-2.5 bg-secondary-850 hover:bg-secondary-800 border border-secondary-800 text-gray-300 hover:text-white rounded-xl text-xs font-display font-semibold uppercase tracking-wider transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadSubmit}
                    disabled={!file}
                    className="flex-1 py-2.5 bg-brand-gradient text-secondary-950 disabled:opacity-40 disabled:pointer-events-none rounded-xl text-xs font-display font-bold uppercase tracking-wider hover:shadow-gold transition-all duration-300 active:scale-[0.98]"
                  >
                    Upload File
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
